import { Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Job } from '../types/job.types';
import { CompanyMetadata } from './company-metadata.service';

export interface EnhancedSignal {
  id: string;
  text: string;
  source: 'resume' | 'chat' | 'feedback' | 'manual';
  confidence: number;
  relatedJobIds: string[];
  createdAt: Date;
}

export interface CalibrationCategory {
  id: string;
  category: string;
  priority: number; // 1-5 importance (5 = most important)
  description: string;
  excellentFit: EnhancedSignal[];
  goodFit: EnhancedSignal[];
  borderline: EnhancedSignal[];
  notAFit: EnhancedSignal[];
}

export interface JobScore {
  totalScore: number;
  breakdown: { 
    category: string; 
    score: number; 
    matchedSignals: string[];
    weight: number;
  }[];
  isDealbreaker: boolean;
  dealbreakerReason?: string;
}

interface SignalsCacheEntry {
  data: CalibrationCategory[];
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class CalibrationService {
  
  // Scoring weights for each tier
  // These are the base scores when a signal matches
  private readonly TIER_WEIGHTS = {
    excellent_fit: 100,  // Perfect match = full score
    good_fit: 70,        // Good match = 70%
    borderline: 30,      // Weak match = 30%
    not_a_fit: -100      // Dealbreaker
  };

  // Company metadata cache (set by SemanticJobSearchService before scoring)
  // Key: company name (lowercase), Value: comprehensive metadata including industries, size, stage, etc.
  private companyMetadata: Map<string, CompanyMetadata> = new Map();

  // Cache for calibration signals (5 minute TTL, keyed by user ID)
  private readonly SIGNALS_CACHE_TTL_MS = 5 * 60 * 1000;
  private signalsCache = new Map<string, SignalsCacheEntry>();
  private pendingSignalsRequests = new Map<string, Promise<CalibrationCategory[]>>();

  constructor(private supabaseService: SupabaseService) {}

  /**
   * Set company metadata for use during scoring
   */
  setCompanyMetadata(metadata: Map<string, CompanyMetadata>): void {
    this.companyMetadata = metadata;
  }

  /**
   * Clear company metadata cache (called after scoring is complete)
   */
  clearCompanyMetadata(): void {
    this.companyMetadata.clear();
  }

  /**
   * Legacy method for backwards compatibility - converts boolean map to metadata
   * @deprecated Use setCompanyMetadata instead
   */
  setIndustryClassifications(classifications: Map<string, boolean>): void {
    // Convert old format to new format for backwards compatibility
    // This is a minimal conversion - full metadata should use setCompanyMetadata
  }

  /**
   * Legacy method for backwards compatibility
   * @deprecated Use clearCompanyMetadata instead
   */
  clearIndustryClassifications(): void {
    this.clearCompanyMetadata();
  }

  /**
   * Get all calibration signals for a user (with caching)
   */
  async getSignals(userId: string): Promise<CalibrationCategory[]> {
    // Check cache first
    const cached = this.signalsCache.get(userId);
    if (cached && Date.now() - cached.timestamp < this.SIGNALS_CACHE_TTL_MS) {
      return cached.data;
    }

    // Deduplicate concurrent requests for the same user
    const pending = this.pendingSignalsRequests.get(userId);
    if (pending) {
      return pending;
    }

    const request = this.fetchSignals(userId);
    this.pendingSignalsRequests.set(userId, request);

    try {
      return await request;
    } finally {
      this.pendingSignalsRequests.delete(userId);
    }
  }

  private async fetchSignals(userId: string): Promise<CalibrationCategory[]> {
    try {
      const { data: signals, error: signalsError } = await this.supabaseService
        .getClient()
        .from('calibration_signals')
        .select(`
          id,
          category,
          priority,
          description,
          calibration_signal_items (
            id,
            tier,
            text,
            source,
            confidence,
            related_job_ids,
            created_at
          )
        `)
        .eq('user_id', userId);

      if (signalsError) {
        throw signalsError;
      }

      const result = (signals || []).map(signal => this.mapToCalibrationCategory(signal));

      // Cache the result
      this.signalsCache.set(userId, {
        data: result,
        timestamp: Date.now(),
      });

      return result;
    } catch {
      return [];
    }
  }

  /**
   * Invalidate cache for a user (call when signals are updated)
   */
  invalidateSignalsCache(userId: string): void {
    this.signalsCache.delete(userId);
  }

  /**
   * Get signals for a specific category
   */
  async getCategorySignals(userId: string, category: string): Promise<CalibrationCategory | null> {
    try {
      const { data: signal, error } = await this.supabaseService
        .getClient()
        .from('calibration_signals')
        .select(`
          id,
          category,
          priority,
          description,
          calibration_signal_items (
            id,
            tier,
            text,
            source,
            confidence,
            related_job_ids,
            created_at
          )
        `)
        .eq('user_id', userId)
        .eq('category', category)
        .single();

      if (error || !signal) {
        return null;
      }

      return this.mapToCalibrationCategory(signal);
    } catch {
      return null;
    }
  }

  /**
   * Update category priority (1-5 scale)
   */
  async updateCategoryPriority(userId: string, category: string, priority: number): Promise<void> {
    try {
      // Ensure category exists first
      await this.ensureCategoryExists(userId, category);

      const { error } = await this.supabaseService
        .getClient()
        .from('calibration_signals')
        .update({ 
          priority,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('category', category);

      if (error) {
        throw error;
      }
    } catch (error) {
      throw new Error(`Failed to update category priority: ${error}`);
    }
  }

  /**
   * Add a new signal to a category (with deduplication)
   */
  async addSignal(
    userId: string, 
    category: string, 
    tier: string, 
    signal: Partial<EnhancedSignal>
  ): Promise<void> {
    try {
      // Ensure category exists
      const categoryId = await this.ensureCategoryExists(userId, category);

      // Check if this exact signal already exists (deduplication)
      const { data: existing } = await this.supabaseService
        .getClient()
        .from('calibration_signal_items')
        .select('id')
        .eq('signal_id', categoryId)
        .eq('tier', tier)
        .eq('text', signal.text || '')
        .maybeSingle();

      if (existing) {
        return;
      }

      const { error } = await this.supabaseService
        .getClient()
        .from('calibration_signal_items')
        .insert({
          signal_id: categoryId,
          tier,
          text: signal.text || '',
          source: signal.source || 'manual',
          confidence: signal.confidence || 1.0,
          related_job_ids: signal.relatedJobIds || []
        });

      if (error) {
        throw error;
      }

      // Invalidate cache since signals changed
      this.invalidateSignalsCache(userId);
    } catch (error) {
      throw new Error(`Failed to add signal: ${error}`);
    }
  }

  /**
   * Remove a signal by ID
   */
  /**
   * Clear signals in a category for a user
   * @param userId - The user ID
   * @param category - The category to clear
   * @param source - Optional: only clear signals from this source (e.g., 'chat' or 'resume')
   *                 If not provided, clears ALL signals in the category
   */
  async clearCategorySignals(userId: string, category: string, source?: string): Promise<void> {
    try {
      // First get the signal category ID from calibration_signals table
      const { data: signalData, error: lookupError } = await this.supabaseService
        .getClient()
        .from('calibration_signals')
        .select('id')
        .eq('user_id', userId)
        .eq('category', category)
        .maybeSingle();

      // If category doesn't exist or error, nothing to clear
      if (lookupError || !signalData) {
        return;
      }

      // Build the delete query
      let deleteQuery = this.supabaseService
        .getClient()
        .from('calibration_signal_items')
        .delete()
        .eq('signal_id', signalData.id);
      
      // If source is specified, only delete signals from that source
      if (source) {
        deleteQuery = deleteQuery.eq('source', source);
      }
      
      const { error } = await deleteQuery;

      if (error) {
        throw error;
      }

      // Invalidate cache since signals changed
      this.invalidateSignalsCache(userId);
    } catch {
      // Silently ignore clear errors
    }
  }

  async removeSignal(signalItemId: string): Promise<void> {
    try {
      const { error } = await this.supabaseService
        .getClient()
        .from('calibration_signal_items')
        .delete()
        .eq('id', signalItemId);

      if (error) {
        throw error;
      }
    } catch (error) {
      throw new Error(`Failed to remove signal: ${error}`);
    }
  }

  /**
   * Remove signals by category and optional text match
   * If text is provided, only removes signals containing that text
   * If text is not provided, removes ALL signals in the category from the given source
   */
  async removeSignalByCategory(
    userId: string, 
    category: string, 
    options?: { text?: string; source?: string }
  ): Promise<void> {
    try {
      // First get the signal category ID
      const { data: signalData, error: lookupError } = await this.supabaseService
        .getClient()
        .from('calibration_signals')
        .select('id')
        .eq('user_id', userId)
        .eq('category', category)
        .maybeSingle();

      if (lookupError || !signalData) {
        return;
      }

      // Build delete query
      let deleteQuery = this.supabaseService
        .getClient()
        .from('calibration_signal_items')
        .delete()
        .eq('signal_id', signalData.id);

      // Filter by source if provided
      if (options?.source) {
        deleteQuery = deleteQuery.eq('source', options.source);
      }

      // Filter by text if provided (using ilike for case-insensitive partial match)
      if (options?.text) {
        deleteQuery = deleteQuery.ilike('text', `%${options.text}%`);
      }

      const { error } = await deleteQuery;

      if (error) {
        throw error;
      }
    } catch (_error) {
      // Error in removeSignalByCategory - swallowed
    }
  }

  /**
   * Score a job against user's calibration signals
   */
  scoreJob(job: Job, signals: CalibrationCategory[], debug: boolean = false): JobScore {
    const breakdown: JobScore['breakdown'] = [];
    let totalScore = 0;
    let isDealbreaker = false;
    let dealbreakerReason: string | undefined;

    // Only count categories that have at least one signal defined
    // Categories with no signals (like "any salary") should not penalize the score
    const categoriesWithSignals = signals.filter(cat => {
      const totalSignals = cat.excellentFit.length + cat.goodFit.length + 
                           cat.borderline.length + cat.notAFit.length;
      return totalSignals > 0;
    });

    // Calculate total priority weight only from categories with signals
    const totalPriorityWeight = categoriesWithSignals.reduce((sum, cat) => sum + cat.priority, 0);

    for (const category of signals) {
      const categoryScore = this.scoreCategoryForJob(job, category, debug);
      
      // Check for dealbreakers first
      if (categoryScore.isDealbreaker) {
        isDealbreaker = true;
        dealbreakerReason = categoryScore.dealbreakerReason;
        break; // Stop processing if we hit a dealbreaker
      }

      // Check if this category has signals
      const categoryHasSignals = category.excellentFit.length + category.goodFit.length + 
                                  category.borderline.length + category.notAFit.length > 0;

      // ROLE MISMATCH DEALBREAKER:
      // If the user has explicit role preferences (from resume OR chat) and the job scores 0,
      // treat it as a dealbreaker - don't show jobs that don't match the user's target roles
      if (category.category === 'role' && categoryHasSignals && categoryScore.score === 0) {
        isDealbreaker = true;
        dealbreakerReason = `Role mismatch: Job title "${job.title}" doesn't match your target roles`;
        break;
      }

      // COMPANY TYPE MISMATCH DEALBREAKER:
      // If the user explicitly asks for a specific industry/company type (e.g., crypto, fintech)
      // and the job doesn't match, treat it as a dealbreaker
      // EXCEPTION: "Any size", "No preference", "Flexible" signals mean user has no preference
      if (category.category === 'company_type' && categoryHasSignals && categoryScore.score === 0) {
        // Check if the only signals are "no preference" type signals
        const allCompanySignals = [
          ...category.excellentFit.map(s => s.text.toLowerCase()),
          ...category.goodFit.map(s => s.text.toLowerCase())
        ];
        const noPreferenceSignals = ['any size', 'any', 'no preference', 'flexible', 'open', 'any company', 'fully remote', 'remote', 'no preference'];
        const hasOnlyNoPreference = allCompanySignals.every(signal => 
          noPreferenceSignals.some(np => signal.includes(np))
        );
        
        // Only mark as dealbreaker if user has actual industry/company type preferences
        if (!hasOnlyNoPreference) {
          isDealbreaker = true;
          dealbreakerReason = `Company type mismatch: "${job.company}" doesn't match your preferred industries (signals: ${allCompanySignals.join(', ')})`;
          break;
        }
      }

      // COMPENSATION MISMATCH DEALBREAKER:
      // If user has a minimum salary requirement and job doesn't meet it, it's a dealbreaker
      // EXCEPTION: "Flexible", "Negotiable", "Open" signals mean user has no strict requirement
      if (category.category === 'compensation' && categoryHasSignals && categoryScore.score === 0) {
        const allCompensationSignals = [
          ...category.excellentFit.map(s => s.text.toLowerCase()),
          ...category.goodFit.map(s => s.text.toLowerCase())
        ];
        const flexibleSignals = ['flexible', 'negotiable', 'open', 'any', 'no preference'];
        const hasOnlyFlexible = allCompensationSignals.every(signal => 
          flexibleSignals.some(flex => signal.includes(flex))
        );
        
        // Only mark as dealbreaker if user has actual salary requirements
        if (!hasOnlyFlexible) {
          isDealbreaker = true;
          dealbreakerReason = `Compensation mismatch: Job salary ($${job.salary_min?.toLocaleString() || '?'} - $${job.salary_max?.toLocaleString() || '?'}) doesn't meet your requirement`;
          break;
        }
      }

      // LOCATION MISMATCH DEALBREAKER:
      // If user has specific location requirements and job doesn't match, it could be a dealbreaker
      // EXCEPTION: "Flexible", "Anywhere", "Any location", "No preference", "Open" signals mean user has no strict requirement
      if (category.category === 'location' && categoryHasSignals && categoryScore.score === 0) {
        const allLocationSignals = [
          ...category.excellentFit.map(s => s.text.toLowerCase()),
          ...category.goodFit.map(s => s.text.toLowerCase())
        ];
        const flexibleSignals = ['flexible', 'anywhere', 'any location', 'any', 'no preference', 'open', 'no restriction'];
        const hasOnlyFlexible = allLocationSignals.every(signal => 
          flexibleSignals.some(flex => signal.includes(flex))
        );
        
        // Only mark as dealbreaker if user has actual location requirements
        // Note: We don't mark location as a hard dealbreaker since remote work is flexible
        // Instead, we just skip the scoring penalty by treating it as "no preference"
        if (hasOnlyFlexible) {
          // If user has only flexible signals, treat it as if they have no location preference
          // Set score to 50 (neutral) so it doesn't penalize or boost the job
          categoryScore.score = 50;
        }
      }

      // Apply priority weighting - only for categories with signals
      // Categories with no signals (e.g., "any salary") don't contribute to score
      const categoryWeight = categoryHasSignals && totalPriorityWeight > 0 
        ? category.priority / totalPriorityWeight 
        : 0;
      const weightedScore = categoryScore.score * categoryWeight;
      
      totalScore += weightedScore;
      
      breakdown.push({
        category: category.category,
        score: categoryScore.score,
        matchedSignals: categoryScore.matchedSignals,
        weight: categoryWeight
      });

    }

    const finalScore = isDealbreaker ? 0 : Math.max(0, Math.min(100, totalScore));

    return {
      totalScore: finalScore,
      breakdown,
      isDealbreaker,
      dealbreakerReason
    };
  }

  /**
   * Filter out jobs that match dealbreaker signals
   */
  filterDealbreakers(jobs: Job[], signals: CalibrationCategory[]): Job[] {
    return jobs.filter(job => {
      const score = this.scoreJob(job, signals);
      return !score.isDealbreaker;
    });
  }

  /**
   * Decay confidence of old signals (called periodically)
   */
  async decayOldSignals(userId: string, decayFactor: number = 0.1): Promise<void> {
    try {
      // Get signals older than 30 days
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const { error } = await this.supabaseService
        .getClient()
        .rpc('decay_signal_confidence', {
          user_id: userId,
          decay_factor: decayFactor,
          cutoff_date: thirtyDaysAgo
        });

      if (error) {
        // Decay failed - ignore
      }
    } catch {
      // Decay process failed - ignore
    }
  }

  /**
   * Initialize default categories for a new user
   */
  async initializeDefaultCategories(userId: string): Promise<void> {
    const defaultCategories = [
      { category: 'location', priority: 5, description: 'Geographic location and remote work preferences' },
      { category: 'work_type', priority: 5, description: 'Onsite or hybrid work arrangement preferences' },
      { category: 'compensation', priority: 4, description: 'Salary and benefits expectations' },
      { category: 'role', priority: 5, description: 'Job title and responsibility preferences' },
      { category: 'company_type', priority: 3, description: 'Company size and type preferences' },
      { category: 'skills', priority: 4, description: 'Technical skills and experience match' }
      // Note: 'culture' removed - not currently collected during onboarding
    ];

    try {
      for (const cat of defaultCategories) {
        try {
          await this.ensureCategoryExists(userId, cat.category, cat.priority, cat.description);
        } catch {
          // Continue with other categories
        }
      }
    } catch {
      // Don't throw - let onboarding continue even if categories fail
    }
  }

  /**
   * Populate calibration signals from user profile data
   * Used to backfill signals for users who completed onboarding before signals were tracked
   */
  async populateSignalsFromProfile(userId: string): Promise<void> {
    try {
      // Get user profile data including preferences for additional data
      const { data: user, error } = await this.supabaseService
        .getClient()
        .from('users')
        .select('job_title, skills, location, experience_years, profile_summary, preferences, work_history')
        .eq('id', userId)
        .single();

      if (error || !user) {
        return;
      }

      // Ensure categories exist
      await this.initializeDefaultCategories(userId);

      // Extract role signals from job_title or preferences
      const jobTitle = user.job_title || 
                       user.preferences?.targetRoles?.[0] || 
                       user.work_history?.[0]?.title;
      
      if (jobTitle) {
        await this.addSignal(userId, 'role', 'excellent_fit', {
          text: jobTitle,
          source: 'resume',
          confidence: 0.9
        });
      }

      // Add additional target roles if available
      const targetRoles = user.preferences?.targetRoles || [];
      for (const role of targetRoles.slice(1, 3)) { // Skip first (already added), take next 2
        await this.addSignal(userId, 'role', 'good_fit', {
          text: role,
          source: 'resume',
          confidence: 0.7
        });
      }

      // Extract seniority from experience_years or preferences
      const seniority = user.preferences?.seniority;
      const experienceYears = user.experience_years || 
                              parseInt(user.preferences?.yearsOfExperience) || 0;
      
      if (seniority) {
        await this.addSignal(userId, 'role', 'excellent_fit', {
          text: `${seniority} positions`,
          source: 'resume',
          confidence: 0.9
        });
      } else if (experienceYears > 0) {
        let derivedSeniority = 'Junior';
        if (experienceYears >= 10) derivedSeniority = 'Lead/Principal';
        else if (experienceYears >= 6) derivedSeniority = 'Senior';
        else if (experienceYears >= 3) derivedSeniority = 'Mid-level';
        
        await this.addSignal(userId, 'role', 'good_fit', {
          text: `${derivedSeniority} level positions`,
          source: 'resume',
          confidence: 0.8
        });
      }

      // Extract all skills - no limit, more skills = better matching
      if (user.skills && user.skills.length > 0) {
        for (const skill of user.skills) {
          await this.addSignal(userId, 'skills', 'good_fit', {
            text: skill,
            source: 'resume',
            confidence: 0.7
          });
        }
      }

      // Extract location if available (but not "Not Specified" placeholder)
      if (user.location && 
          user.location.toLowerCase() !== 'not specified' && 
          user.location.toLowerCase() !== 'unknown') {
        await this.addSignal(userId, 'location', 'excellent_fit', {
          text: user.location,
          source: 'resume',
          confidence: 0.8
        });
      }

      // Check preferred work mode from preferences
      const workMode = user.preferences?.preferredWorkMode;
      if (workMode && workMode.toLowerCase() !== 'not specified') {
        await this.addSignal(userId, 'location', 'excellent_fit', {
          text: workMode,
          source: 'resume',
          confidence: 0.8
        });
      }
    } catch {
      // Populate failed - ignore
    }
  }

  /**
   * Ensure a category exists for the user, create if not
   */
  async ensureCategoryExists(
    userId: string, 
    category: string, 
    priority: number = 3,
    description: string = ''
  ): Promise<string> {
    try {
      // Try to get existing category (don't use .single() to avoid error on no results)
      const { data: existing, error: selectError } = await this.supabaseService
        .getClient()
        .from('calibration_signals')
        .select('id')
        .eq('user_id', userId)
        .eq('category', category)
        .maybeSingle();

      // If found, return existing ID
      if (existing) {
        return existing.id;
      }

      // Create new category
      const { data: newCategory, error: insertError } = await this.supabaseService
        .getClient()
        .from('calibration_signals')
        .insert({
          user_id: userId,
          category,
          priority,
          description
        })
        .select('id')
        .single();

      if (insertError) {
        throw insertError;
      }

      return newCategory!.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Score a single category against a job
   */
  private scoreCategoryForJob(job: Job, category: CalibrationCategory, debug: boolean = false): {
    score: number;
    matchedSignals: string[];
    isDealbreaker: boolean;
    dealbreakerReason?: string;
  } {
    const matchedSignals: string[] = [];
    let score = 0;

    const totalSignals = category.excellentFit.length + category.goodFit.length + 
                         category.borderline.length + category.notAFit.length;

    // Check dealbreakers first
    for (const signal of category.notAFit) {
      if (this.signalMatchesJob(signal.text, job, category.category, debug)) {
        if (debug) {
          console.log(`❌ DEALBREAKER: Job "${job.title}" at "${job.company}" filtered by signal "${signal.text}" in category "${category.category}"`);
        }
        return {
          score: 0,
          matchedSignals: [signal.text],
          isDealbreaker: true,
          dealbreakerReason: `${category.category}: ${signal.text}`
        };
      }
    }

    // LOCATION EXCLUSIVITY: Check if job matches location signals from chat
    // Note: This now handles geographic location AND remote (but NOT onsite/hybrid - those are in work_type)
    // IMPORTANT: ALL location signals must match (not just one)
    if (category.category === 'location') {
      // Get all excellent_fit location signals (these are what user explicitly asked for)
      const locationSignals = category.excellentFit.filter(s => s.source === 'chat');

      if (locationSignals.length > 0) {
        const jobWorkType = (job.work_type || '').toLowerCase();
        const jobLocation = (job.display_location || job.location || '').toLowerCase();
        
        // Check if job matches ALL location signals (not just one)
        // Example: If user has ["remote", "Portugal"], job must be BOTH remote AND in Portugal
        let allSignalsMatch = true;
        const unmatchedSignals: string[] = [];
        
        for (const signal of locationSignals) {
          const signalText = signal.text.toLowerCase();
          let thisSignalMatches = false;

          // Check for "remote" signal
          if (signalText === 'remote') {
            thisSignalMatches = jobWorkType === 'remote' || jobWorkType.includes('remote');
            if (!thisSignalMatches) {
              unmatchedSignals.push('remote');
              allSignalsMatch = false;
            }
            continue;
          }

          // For geographic location signals
          // Extract location keywords from signal
          const nonLocationWords = [
            'remote', 'onsite', 'hybrid', 'on-site', 'in-office',
            'fully', 'completely', '100%', 'work', 'preference', 'flexible',
            'anywhere', 'worldwide', 'global', 'international'
          ];
          const signalWords = signalText
            .replace(/jobs?|in|or|and|the|for|at|only|show|me|roles/gi, ' ')
            .split(/\s+/)
            .filter(w => w.length > 1 && !nonLocationWords.includes(w));

          if (signalWords.length > 0) {
            // Expand continent keywords to countries for matching
            const expandedLocationKeywords = this.expandContinentKeywords(signalWords);

            // Check if job location matches this geographic signal
            thisSignalMatches = expandedLocationKeywords.some(keyword =>
              jobLocation.includes(keyword.toLowerCase())
            );

            // If no direct match, check if job is in a city within the requested state
            if (!thisSignalMatches) {
              thisSignalMatches = this.isCityInState(jobLocation, expandedLocationKeywords);
            }

            if (!thisSignalMatches) {
              unmatchedSignals.push(signalText);
              allSignalsMatch = false;
            }
          }
        }
        
        if (!allSignalsMatch) {
          const signalTexts = locationSignals.map(s => s.text).join(' + ');
          return {
            score: 0,
            matchedSignals: [signalTexts],
            isDealbreaker: true,
            dealbreakerReason: `Location mismatch: wanted "${signalTexts}", job is ${jobWorkType} in ${jobLocation || 'unknown location'}`
          };
        }
      }
    }

    // WORK_TYPE EXCLUSIVITY: Check if job matches work_type signals (onsite/hybrid) from chat
    if (category.category === 'work_type') {
      const workTypeSignals = category.excellentFit.filter(s => s.source === 'chat');

      if (workTypeSignals.length > 0) {
        const jobWorkType = (job.work_type || '').toLowerCase();
        
        // Check if any work_type signal matches this job
        let matchesAnySignal = false;
        for (const signal of workTypeSignals) {
          const signalText = signal.text.toLowerCase();

          if (signalText.includes('onsite') || signalText.includes('on-site') || signalText.includes('in-office')) {
            matchesAnySignal = jobWorkType === 'onsite' || jobWorkType === 'on-site';
          } else if (signalText.includes('hybrid')) {
            matchesAnySignal = jobWorkType === 'hybrid';
          }
          
          if (matchesAnySignal) break;
        }
        
        if (!matchesAnySignal) {
          const signalTexts = workTypeSignals.map(s => s.text).join(', ');
          return {
            score: 0,
            matchedSignals: [signalTexts],
            isDealbreaker: true,
            dealbreakerReason: `Work type mismatch: wanted "${signalTexts}", job is ${jobWorkType || 'unknown'}`
          };
        }
      }
    }

    // Score positive signals
    const allSignals = [
      ...category.excellentFit.map(s => ({ ...s, tier: 'excellent_fit' })),
      ...category.goodFit.map(s => ({ ...s, tier: 'good_fit' })),
      ...category.borderline.map(s => ({ ...s, tier: 'borderline' }))
    ];

    let matchCount = 0;
    for (const signal of allSignals) {
      if (this.signalMatchesJob(signal.text, job, category.category, debug)) {
        const tierWeight = this.TIER_WEIGHTS[signal.tier as keyof typeof this.TIER_WEIGHTS];
        const confidenceWeight = signal.confidence || 1.0;
        score += tierWeight * confidenceWeight;
        matchedSignals.push(signal.text);
        matchCount++;
      }
    }

    // EXPERIENCE-BASED COMPENSATION for skills category
    // Trigger based on match rate OR absolute match count, not just score
    // Rationale: Experienced designers can often pick up new tools/skills quickly
    if (category.category === 'skills') {
      const totalUserSkills = allSignals.length;
      const matchRate = totalUserSkills > 0 ? matchCount / totalUserSkills : 0;
      const shouldApplyBonus = matchRate < 0.20 || matchCount < 3;
      
      if (shouldApplyBonus && totalUserSkills > 0) {
        const experienceBonus = this.calculateExperienceBonus(job, category, debug);
        if (experienceBonus > 0) {
          score += experienceBonus;
          matchedSignals.push(`Experience bonus: +${experienceBonus.toFixed(0)} points`);
        }
      }
    }

    // Cap score at 100 (multiple matches shouldn't exceed 100%)
    return {
      score: Math.min(100, Math.max(0, score)),
      matchedSignals,
      isDealbreaker: false
    };
  }

  /**
   * Calculate experience-based bonus for skills when explicit matches are low
   * Senior/Lead designers can often adapt to new tools and methodologies
   */
  private calculateExperienceBonus(
    job: Job, 
    skillsCategory: CalibrationCategory,
    debug: boolean = false
  ): number {
    // Define seniority levels and their "adaptability" scores
    const seniorityLevels: Record<string, number> = {
      'entry': 1,
      'junior': 2,
      'mid': 3,
      'mid-level': 3,
      'senior': 4,
      'lead': 5,
      'principal': 5,
      'staff': 5,
      'manager': 4,
      'director': 5,
      'vp': 5,
      'head': 5,
      'chief': 5,
      'cxo': 5,
      'executive': 5
    };

    // Skills that indicate senior experience
    const seniorSkillIndicators = [
      'leadership', 'mentoring', 'team management', 'strategic', 'strategy',
      'stakeholder', 'cross-functional', 'design systems', 'architecture',
      'vision', 'roadmap', 'coaching', 'hiring', 'scaling'
    ];

    // Get user's implied seniority from their skill signals
    let userSeniorityLevel = 0;
    const allSkillSignals = [
      ...skillsCategory.excellentFit,
      ...skillsCategory.goodFit,
      ...skillsCategory.borderline
    ];

    // Check skill signals for seniority indicators
    let seniorSkillCount = 0;
    for (const signal of allSkillSignals) {
      const signalLower = signal.text.toLowerCase();
      
      // Check for explicit seniority mentions
      for (const [level, score] of Object.entries(seniorityLevels)) {
        if (signalLower.includes(level)) {
          userSeniorityLevel = Math.max(userSeniorityLevel, score);
        }
      }
      
      // Check for senior-indicating skills
      for (const indicator of seniorSkillIndicators) {
        if (signalLower.includes(indicator)) {
          seniorSkillCount++;
          break;
        }
      }
    }

    // If they have 3+ senior-indicating skills, treat as senior level
    if (seniorSkillCount >= 3 && userSeniorityLevel < 4) {
      userSeniorityLevel = 4;
    } else if (seniorSkillCount >= 1 && userSeniorityLevel < 3) {
      userSeniorityLevel = 3;
    }

    // If we still can't determine seniority, no bonus
    if (userSeniorityLevel < 3) {
      return 0;
    }

    // Get job's required seniority
    const jobLevel = (job.level || '').toLowerCase();
    const jobTitle = (job.title || '').toLowerCase();
    let jobSeniorityLevel = 3; // Default to mid-level if not specified
    
    for (const [level, score] of Object.entries(seniorityLevels)) {
      if (jobLevel.includes(level) || jobTitle.includes(level)) {
        jobSeniorityLevel = Math.max(jobSeniorityLevel, score);
        break;
      }
    }

    // Calculate bonus based on experience gap
    // If user is more senior than job requires, they get a bonus
    const seniorityGap = userSeniorityLevel - jobSeniorityLevel;
    
    if (seniorityGap >= 0 && userSeniorityLevel >= 4) {
      // Senior+ users get bonus when applying to same or lower level jobs
      // Max bonus: 40 points (covers up to 40% of skills gap)
      const bonus = Math.min(40, (seniorityGap + 1) * 15);
      return bonus;
    }

    return 0;
  }

  /**
   * Check if a job location (city) is within a requested state/region/country
   */
  private isCityInState(jobLocation: string, requestedLocations: string[]): boolean {
    // Map of US states to major cities
    const stateToCities: Record<string, string[]> = {
      'texas': ['houston', 'dallas', 'austin', 'san antonio', 'fort worth', 'el paso', 'plano', 'irving', 'arlington', 'frisco', 'mckinney', 'denton'],
      'california': ['los angeles', 'san francisco', 'san diego', 'san jose', 'fresno', 'sacramento', 'oakland', 'long beach', 'irvine', 'palo alto', 'mountain view', 'sunnyvale', 'santa clara', 'cupertino'],
      'new york': ['new york city', 'nyc', 'brooklyn', 'manhattan', 'queens', 'bronx', 'staten island', 'buffalo', 'rochester', 'albany', 'syracuse'],
      'florida': ['miami', 'orlando', 'tampa', 'jacksonville', 'fort lauderdale', 'west palm beach', 'st petersburg', 'clearwater'],
      'washington': ['seattle', 'tacoma', 'bellevue', 'spokane', 'redmond', 'kirkland'],
      'massachusetts': ['boston', 'cambridge', 'worcester', 'springfield', 'lowell'],
      'illinois': ['chicago', 'aurora', 'naperville', 'joliet', 'rockford'],
      'georgia': ['atlanta', 'augusta', 'savannah', 'columbus', 'macon'],
      'colorado': ['denver', 'colorado springs', 'aurora', 'boulder', 'fort collins'],
      'pennsylvania': ['philadelphia', 'pittsburgh', 'allentown', 'reading', 'erie'],
      'arizona': ['phoenix', 'tucson', 'mesa', 'scottsdale', 'chandler', 'tempe'],
      'ohio': ['columbus', 'cleveland', 'cincinnati', 'toledo', 'akron'],
      'michigan': ['detroit', 'grand rapids', 'ann arbor', 'lansing', 'flint'],
      'north carolina': ['charlotte', 'raleigh', 'durham', 'greensboro', 'winston-salem', 'cary'],
      'virginia': ['richmond', 'virginia beach', 'norfolk', 'chesapeake', 'arlington', 'alexandria', 'mclean', 'tysons'],
      'maryland': ['baltimore', 'columbia', 'germantown', 'silver spring', 'bethesda', 'rockville'],
      'oregon': ['portland', 'salem', 'eugene', 'gresham', 'hillsboro', 'beaverton'],
      'minnesota': ['minneapolis', 'st paul', 'saint paul', 'rochester', 'bloomington'],
      'utah': ['salt lake city', 'salt lake', 'west valley city', 'provo', 'sandy', 'lehi']
    };

    // All US cities combined for country-level matching
    const allUSCities = Object.values(stateToCities).flat();
    
    // US state abbreviations in job locations (e.g., "New York, NY")
    const usStateAbbreviations = [
      'al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga',
      'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md',
      'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj',
      'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc',
      'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy', 'dc'
    ];

    const jobLocationLower = jobLocation.toLowerCase();
    
    // Check for country-level matching (e.g., "US" should match any US city)
    const usCountryKeywords = ['united states', 'usa', 'america', 'united states of america'];
    const isUSCountrySearch = requestedLocations.some(loc => 
      usCountryKeywords.includes(loc.toLowerCase())
    );
    
    if (isUSCountrySearch) {
      // Check if job location contains any US city
      if (allUSCities.some(city => jobLocationLower.includes(city))) {
        return true;
      }
      // Check if job location ends with a US state abbreviation (e.g., "New York, NY")
      const locationParts = jobLocationLower.split(',').map(p => p.trim());
      if (locationParts.length >= 2) {
        const lastPart = locationParts[locationParts.length - 1];
        if (usStateAbbreviations.includes(lastPart)) {
          return true;
        }
      }
    }
    
    for (const location of requestedLocations) {
      const locationLower = location.toLowerCase();
      
      // Skip country-level keywords (already handled above)
      if (usCountryKeywords.includes(locationLower)) {
        continue;
      }
      
      // Check if the requested location is a state
      if (stateToCities[locationLower]) {
        // Check if job is in any city in that state
        if (stateToCities[locationLower].some(city => jobLocationLower.includes(city))) {
          return true;
        }
      }
      
      // Also check state abbreviations and full names
      const stateAbbreviationsMap: Record<string, string> = {
        'tx': 'texas', 'ca': 'california', 'ny': 'new york', 'fl': 'florida',
        'wa': 'washington', 'ma': 'massachusetts', 'il': 'illinois', 'ga': 'georgia',
        'co': 'colorado', 'pa': 'pennsylvania', 'az': 'arizona', 'oh': 'ohio',
        'mi': 'michigan', 'nc': 'north carolina', 'va': 'virginia', 'md': 'maryland',
        'or': 'oregon', 'mn': 'minnesota', 'ut': 'utah'
      };
      
      const stateName = stateAbbreviationsMap[locationLower] || locationLower;
      if (stateToCities[stateName]) {
        if (stateToCities[stateName].some(city => jobLocationLower.includes(city))) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Expand continent keywords to their constituent countries for location matching
   */
  private expandContinentKeywords(keywords: string[]): string[] {
    const continentExpansion: Record<string, string[]> = {
      // US-specific expansions - only unambiguous terms
      // Note: 'united' and 'states' are NOT expanded because they appear in
      // "United Kingdom", "United Arab Emirates", etc. which would cause false matches
      'us': ['united states', 'usa', 'america', 'united states of america'],
      'usa': ['united states', 'usa', 'america', 'united states of america'],
      'america': ['united states', 'usa', 'america', 'united states of america'],
      // UK-specific expansions
      'uk': ['united kingdom', 'great britain', 'britain', 'england', 'scotland', 'wales'],
      'britain': ['united kingdom', 'great britain', 'britain', 'england', 'scotland', 'wales'],
      'england': ['united kingdom', 'england', 'london'],
      // UAE
      'uae': ['united arab emirates', 'dubai', 'abu dhabi'],
      'dubai': ['united arab emirates', 'dubai', 'uae'],
      'europe': [
        // Western Europe
        'united kingdom', 'uk', 'gb', 'great britain', 'britain', 'england', 
        'scotland', 'wales', 'northern ireland', 'ireland', 'republic of ireland',
        'france', 'germany', 'netherlands', 'holland', 'belgium', 'luxembourg', 
        'austria', 'switzerland', 'liechtenstein', 'monaco',
        // Southern Europe
        'spain', 'portugal', 'italy', 'greece', 'malta', 'cyprus', 'andorra',
        'san marino', 'vatican', 'gibraltar',
        // Northern Europe (Nordics)
        'sweden', 'norway', 'denmark', 'finland', 'iceland', 'faroe islands',
        // Central Europe
        'poland', 'czech republic', 'czechia', 'czech', 'slovakia', 'hungary', 'slovenia',
        // Eastern Europe / Balkans
        'romania', 'bulgaria', 'croatia', 'serbia', 'bosnia', 'herzegovina',
        'bosnia and herzegovina', 'montenegro', 'north macedonia', 'macedonia',
        'albania', 'kosovo', 'moldova', 'ukraine', 'belarus',
        // Baltic States
        'estonia', 'latvia', 'lithuania',
        // Caucasus (sometimes considered Europe)
        'georgia', 'armenia', 'azerbaijan',
        // Major European cities (for partial matches)
        'london', 'paris', 'berlin', 'amsterdam', 'barcelona', 'madrid', 'rome',
        'milan', 'munich', 'dublin', 'lisbon', 'vienna', 'prague', 'warsaw',
        'brussels', 'copenhagen', 'stockholm', 'oslo', 'helsinki', 'zurich',
        'geneva', 'edinburgh', 'manchester', 'birmingham'
      ],
      'asia': [
        'japan', 'singapore', 'india', 'china', 'south korea', 'korea', 'taiwan', 
        'hong kong', 'thailand', 'vietnam', 'malaysia', 'indonesia', 'philippines',
        'pakistan', 'bangladesh', 'sri lanka', 'nepal', 'uae', 'dubai', 'israel',
        'turkey', 'saudi arabia', 'qatar', 'kuwait', 'bahrain', 'oman'
      ],
      'latam': [
        'brazil', 'mexico', 'argentina', 'colombia', 'chile', 'peru', 
        'costa rica', 'uruguay', 'ecuador', 'venezuela', 'panama', 'guatemala',
        'honduras', 'el salvador', 'nicaragua', 'paraguay', 'bolivia',
        'dominican republic', 'puerto rico', 'cuba', 'jamaica'
      ],
      'latin america': [
        'brazil', 'mexico', 'argentina', 'colombia', 'chile', 'peru', 
        'costa rica', 'uruguay', 'ecuador', 'venezuela', 'panama', 'guatemala',
        'honduras', 'el salvador', 'nicaragua', 'paraguay', 'bolivia',
        'dominican republic', 'puerto rico', 'cuba', 'jamaica'
      ],
      'north america': [
        'united states', 'usa', 'us', 'america', 'canada'
      ],
      'oceania': [
        'australia', 'new zealand'
      ],
      'africa': [
        'south africa', 'nigeria', 'kenya', 'egypt', 'morocco', 'ghana',
        'tunisia', 'rwanda', 'ethiopia', 'tanzania', 'uganda'
      ]
    };

    const expanded: string[] = [];
    for (const keyword of keywords) {
      const lowerKeyword = keyword.toLowerCase();
      const expansion = continentExpansion[lowerKeyword];
      if (expansion) {
        expanded.push(...expansion);
      } else {
        expanded.push(keyword);
      }
    }
    return expanded;
  }

  /**
   * Check if a signal matches a job based on category
   */
  private signalMatchesJob(signalText: string, job: Job, category: string, debug: boolean = false): boolean {
    const signal = signalText.toLowerCase();
    
    switch (category) {
      case 'location':
        return this.matchesLocation(signal, job);
      case 'work_type':
        return this.matchesWorkType(signal, job);
      case 'role':
        return this.matchesRole(signal, job);
      case 'company_type':
        return this.matchesCompanyType(signal, job, debug);
      case 'compensation':
        return this.matchesCompensation(signal, job);
      case 'skills':
        return this.matchesSkills(signal, job);
      case 'culture':
        return this.matchesCulture(signal, job);
      default:
        return this.matchesGeneral(signal, job);
    }
  }

  private matchesLocation(signal: string, job: Job): boolean {
    const jobLocation = [job.location, job.display_location]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const jobWorkType = (job.work_type || '').toLowerCase();
    
    // Extract location keywords from signal (remove common words and non-location terms)
    // Note: We keep "remote" here since remote is part of location category
    // But onsite/hybrid are handled by work_type category separately
    const nonLocationWords = [
      'fully', 'completely', '100%', 'work', 'preference', 'flexible',
      'anywhere', 'worldwide', 'global', 'international'
    ];
    const signalWords = signal
      .replace(/jobs?|in|or|and|the|for|at/gi, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !nonLocationWords.includes(w));
    
    // Check for remote match (remote is part of location, not work_type)
    // onsite/hybrid are NOT handled here - they go to work_type category
    let isRemoteSignal = signal.includes('remote');
    let remoteMatch = false;
    if (isRemoteSignal) {
      remoteMatch = jobWorkType === 'remote' || jobWorkType.includes('remote');
    }
    
    // Check location match (country, city names)
    const locationMatch = signalWords.some(word => {
      // Skip work type words (remote is included here for keyword extraction)
      if (['remote', 'onsite', 'hybrid', 'on-site'].includes(word)) {
        return false;
      }
      return jobLocation.includes(word);
    });
    
    // Determine if signal has a geographic location component
    const hasLocationWord = signalWords.some(w => 
      !['remote', 'onsite', 'hybrid', 'on-site'].includes(w)
    );
    
    if (hasLocationWord) {
      // Signal has a geographic location (e.g., "France", "remote in Europe")
      if (isRemoteSignal) {
        // "remote in France" - need both remote AND geographic match
        return remoteMatch && locationMatch;
      }
      // Just geographic location (e.g., "France") - only need location match
      return locationMatch;
    }
    
    // Signal is "remote" only (no geographic component)
    if (isRemoteSignal) {
      return remoteMatch;
    }
    
    // Fallback: check if signal text appears in job location
    return jobLocation.includes(signal);
  }

  /**
   * Match work_type signals (onsite, hybrid) against job's work_type field
   * Note: "remote" is NOT handled here - it goes in location category
   */
  private matchesWorkType(signal: string, job: Job): boolean {
    const jobWorkType = (job.work_type || '').toLowerCase();
    
    // Check for onsite match
    if (signal.includes('onsite') || signal.includes('on-site') || signal.includes('in-office')) {
      return jobWorkType === 'onsite' || jobWorkType === 'on-site';
    }
    
    // Check for hybrid match
    if (signal.includes('hybrid')) {
      return jobWorkType === 'hybrid';
    }
    
    // If signal is something else, try direct match
    return jobWorkType.includes(signal);
  }

  private matchesRole(signal: string, job: Job): boolean {
    const roleFields = [
      job.title,
      job.level,
      ...(job.responsibilities || []),
      ...(job.requirements || [])
    ].filter(Boolean).map(r => r!.toLowerCase());

    // First try exact/substring match
    if (roleFields.some(field => field.includes(signal) || signal.includes(field))) {
      return true;
    }
    
    // Extract core role keywords from signal (remove seniority prefixes and common words)
    const seniorityWords = ['senior', 'junior', 'lead', 'principal', 'staff', 'mid', 'entry', 'intern', 'head', 'chief', 'director', 'manager', 'vp', 'associate'];
    const commonWords = ['level', 'position', 'role', 'positions', 'roles', 'at', 'for', 'the', 'a', 'an', 'in', 'of'];
    
    const signalKeywords = signal
      .split(/\s+/)
      .filter(word => word.length > 1 && !seniorityWords.includes(word) && !commonWords.includes(word));
    
    // Check if ALL core keywords from signal appear in any role field
    // e.g., "product designer" from "Senior Product Designer" should match "Product Designer at fast-growing..."
    if (signalKeywords.length > 0) {
      const jobTitleLower = (job.title || '').toLowerCase();
      const allKeywordsMatch = signalKeywords.every(keyword => jobTitleLower.includes(keyword));
      if (allKeywordsMatch) {
        return true;
      }
    }
    
    return false;
  }

  private matchesCompanyType(signal: string, job: Job, debug: boolean = false): boolean {
    const companyKey = (job.company || '').toLowerCase().trim();
    const signalLower = signal.toLowerCase();
    
    // Check for company name exclusion patterns
    // Examples: "avoid Passion.io", "Passion.io", "not Passion.io"
    // When in not_a_fit tier, these should match to EXCLUDE the company
    const exclusionKeywords = ['avoid', 'not', 'exclude', 'filter out'];
    const hasExclusionKeyword = exclusionKeywords.some(keyword => signalLower.includes(keyword));
    
    // Extract potential company name from signal
    // Remove exclusion keywords and common words to get the company name
    let potentialCompanyName = signalLower;
    for (const keyword of exclusionKeywords) {
      potentialCompanyName = potentialCompanyName.replace(new RegExp(`\\b${keyword}\\b`, 'gi'), '').trim();
    }
    
    // Remove common filler words
    potentialCompanyName = potentialCompanyName
      .replace(/\b(companies?|positions?|jobs?|all|from)\b/gi, '')
      .trim();
    
    // If we have a potential company name and it matches the job's company, return true
    // This means the signal matches this job (for exclusion when in not_a_fit tier)
    if (potentialCompanyName.length > 0 && companyKey.length > 0) {
      // Check for exact match or partial match (case-insensitive)
      const companyNameNormalized = potentialCompanyName.replace(/[.\s-]/g, '').toLowerCase();
      const jobCompanyNormalized = companyKey.replace(/[.\s-]/g, '').toLowerCase();
      
      if (jobCompanyNormalized.includes(companyNameNormalized) || 
          companyNameNormalized.includes(jobCompanyNormalized)) {
        if (debug) {
          console.log(`🚫 Company filter match: Signal "${signal}" matches job company "${job.company}"`);
        }
        return true; // Signal matches this job (will be excluded if in not_a_fit tier)
      }
    }
    
    // Check if we have company metadata from the database
    const metadata = this.companyMetadata.get(companyKey);
    
    if (metadata) {
      // Use comprehensive metadata matching
      const matchResult = this.matchesCompanyMetadata(metadata, signal, job);
      if (matchResult.definitive) {
        return matchResult.matches;
      }
    }

    // FALLBACK: Check structured fields on the job itself (company_size and company_type)
    const companySize = (job.company_size || '').toLowerCase();
    const companyType = (job.company_type || '').toLowerCase();
    
    // Direct match on structured fields
    if (companySize && companySize.includes(signal)) {
      return true;
    }
    if (companyType && companyType.includes(signal)) {
      return true;
    }
    
    // For industry/domain signals, be STRICT when no metadata exists
    // Only match on specific industry keywords in LIMITED fields (company name, about_company)
    // Generic words like "human", "hiring", "job", "talent" should NOT trigger matches
    
    // Define strict industry keywords that are unlikely to appear generically
    const strictIndustryKeywords = [
      // Keep short industry acronyms too (match in company/about_company only)
      'ai', 'ml', 'llm', 'hr',
      'crypto', 'cryptocurrency', 'blockchain', 'web3', 'defi', 'nft',
      'fintech', 'banking', 'payments',
      'healthcare', 'medical', 'biotech', 'pharma',
      'gaming', 'casino', 'gambling',
      'saas', 'enterprise',
      'ecommerce', 'marketplace',
      'edtech', 'education',
      'proptech', 'real estate',
      'logistics', 'shipping',
      'cybersecurity', 'security',
      'legal', 'legaltech',
      'agtech', 'agriculture',
      'cleantech', 'climate',
      'hrtech', 'recruiting platform', 'ats', 'applicant tracking',
      'staffing agency', 'recruitment agency', 'talent platform'
    ];
    
    // Split signal into keywords
    // Include 2-letter keywords like "ai" and "hr"
    const signalKeywords = signal.split(/\s+/).filter(w => w.length > 1);
    
    // Check for strict industry keyword matches ONLY
    // These are specific enough to indicate industry, not generic job posting words
    const strictKeywordsInSignal = signalKeywords.filter(k => 
      strictIndustryKeywords.some(sk => sk.includes(k) || k === sk)
    );
    
    // Only check company name and about_company (NOT job descriptions which have generic words)
    const companyContext = [
      job.company,
      job.about_company
    ].filter(Boolean).map(f => f!.toLowerCase()).join(' ');
    
    // Check if any STRICT keyword matches in company context
    for (const keyword of strictKeywordsInSignal) {
      const wordBoundaryRegex = new RegExp(`\\b${keyword}\\b`, 'i');
      if (wordBoundaryRegex.test(companyContext)) {
        return true;
      }
    }
    
    // For generic keywords (human, hiring, talent, job, etc.), DON'T match on job descriptions
    // These would match almost every job posting
    // Instead, only consider them if they appear in company NAME specifically
    const genericKeywords = ['human', 'talent', 'hiring', 'job', 'portal', 'resources', 'recruitment'];
    const hasGenericKeywords = signalKeywords.some(k => genericKeywords.includes(k));
    
    if (hasGenericKeywords) {
      // Check only company name for multi-word matches like "human resources" 
      const companyName = (job.company || '').toLowerCase();
      
      // Try matching multi-word phrases that are more specific
      const specificPhrases = ['human resources', 'hr tech', 'talent acquisition', 
        'recruitment', 'staffing', 'hiring platform', 'job board', 'job portal'];
      
      for (const phrase of specificPhrases) {
        if (companyName.includes(phrase) || (job.about_company || '').toLowerCase().includes(phrase)) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Match a signal against comprehensive company metadata
   */
  private matchesCompanyMetadata(
    metadata: CompanyMetadata, 
    signal: string, 
    job: Job
  ): { matches: boolean; definitive: boolean; reason: string } {
    // Note: k.length > 1 to include 2-letter words like "ai", "hr"
    const keywords = signal.toLowerCase().split(/\s+/).filter(k => k.length > 1);
    
    // Try matching against different metadata categories
    // Start with industry (most common use case)
    const industryMatch = this.matchesIndustryMetadata(metadata, keywords);
    if (industryMatch.matches) {
      return { matches: true, definitive: true, reason: industryMatch.reason };
    }

    // Check company size
    const sizeMatch = this.matchesSizeMetadata(metadata, keywords);
    if (sizeMatch.matches) {
      return { matches: true, definitive: true, reason: sizeMatch.reason };
    }

    // Check company stage
    const stageMatch = this.matchesStageMetadata(metadata, keywords);
    if (stageMatch.matches) {
      return { matches: true, definitive: true, reason: stageMatch.reason };
    }

    // Check remote culture
    const cultureMatch = this.matchesCultureMetadata(metadata, keywords);
    if (cultureMatch.matches) {
      return { matches: true, definitive: true, reason: cultureMatch.reason };
    }

    // Check work style
    const workStyleMatch = this.matchesWorkStyleMetadata(metadata, keywords);
    if (workStyleMatch.matches) {
      return { matches: true, definitive: true, reason: workStyleMatch.reason };
    }

    // Check benefits
    const benefitsMatch = this.matchesBenefitsMetadata(metadata, keywords);
    if (benefitsMatch.matches) {
      return { matches: true, definitive: true, reason: benefitsMatch.reason };
    }

    // If we have metadata but nothing matched, it's a definitive NO for industry-type queries
    // But only if the signal looks like an industry query
    const industryKeywords = ['crypto', 'fintech', 'ai', 'saas', 'gaming', 'healthcare', 
      'blockchain', 'web3', 'startup', 'enterprise', 'social', 'ecommerce',
      'hr', 'talent', 'recruitment', 'hiring', 'staffing', 'consulting', 'education', 'media', 'marketing'];
    const isIndustryQuery = keywords.some(k => industryKeywords.some(ik => k.includes(ik) || ik.includes(k)));
    
    if (isIndustryQuery && metadata.industries.length > 0) {
      return { 
        matches: false, 
        definitive: true, 
        reason: `Industries [${metadata.industries.join(', ')}] don't match signal` 
      };
    }

    // Not definitive - fall back to text search
    return { matches: false, definitive: false, reason: 'No metadata match, using fallback' };
  }

  /**
   * Check if company industries match signal keywords
   */
  private matchesIndustryMetadata(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason: string } {
    const keywordToIndustry: { [key: string]: string[] } = {
      // Crypto/Web3
      'crypto': ['crypto'],
      'cryptocurrency': ['crypto'],
      'blockchain': ['crypto'],
      'web3': ['crypto'],
      'nft': ['crypto'],
      'defi': ['crypto'],
      'staking': ['crypto'],
      'bitcoin': ['crypto'],
      'ethereum': ['crypto'],
      'solana': ['crypto'],
      'decentralized': ['crypto'],
      // Fintech
      'fintech': ['fintech'],
      'financial': ['fintech'],
      'banking': ['fintech'],
      'payments': ['fintech'],
      // AI
      'ai': ['ai'],
      'artificial': ['ai'],
      'intelligence': ['ai'],
      'machine': ['ai'],
      'learning': ['ai'],
      // HR/Talent/Recruitment
      'hr': ['hr'],
      'human': ['hr'],
      'resources': ['hr'],
      'talent': ['hr'],
      'recruitment': ['hr'],
      'recruiting': ['hr'],
      'hiring': ['hr'],
      'staffing': ['hr'],
      'ats': ['hr'],
      'applicant': ['hr'],
      'job': ['hr'],  // job portal, job board
      'portal': ['hr'],
      // Other
      'saas': ['saas'],
      'ecommerce': ['ecommerce'],
      'gaming': ['gaming'],
      'game': ['gaming'],
      'healthcare': ['healthcare'],
      'health': ['healthcare'],
      'social': ['social'],
      'enterprise': ['enterprise'],
      'consulting': ['consulting'],
      'education': ['education'],
      'media': ['media'],
      'marketing': ['marketing'],
    };

    for (const keyword of keywords) {
      const mappedIndustries = keywordToIndustry[keyword];
      if (mappedIndustries) {
        for (const industry of mappedIndustries) {
          if (metadata.industries.includes(industry)) {
            return { matches: true, reason: `Industry: ${industry}` };
          }
        }
      }
    }

    return { matches: false, reason: 'No industry match' };
  }

  /**
   * Check if company size matches signal keywords
   */
  private matchesSizeMetadata(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason: string } {
    if (!metadata.company_size) {
      return { matches: false, reason: 'Size unknown' };
    }

    const sizeKeywords: { [key: string]: string[] } = {
      'startup': ['startup'],
      'small': ['small', 'startup'],
      'medium': ['medium'],
      'large': ['large'],
      'enterprise': ['enterprise', 'large'],
      'big': ['large', 'enterprise'],
    };

    for (const keyword of keywords) {
      const validSizes = sizeKeywords[keyword];
      if (validSizes && validSizes.includes(metadata.company_size)) {
        return { matches: true, reason: `Size: ${metadata.company_size}` };
      }
    }

    return { matches: false, reason: 'Size mismatch' };
  }

  /**
   * Check if company stage matches signal keywords
   */
  private matchesStageMetadata(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason: string } {
    if (!metadata.company_stage) {
      return { matches: false, reason: 'Stage unknown' };
    }

    const stageKeywords: { [key: string]: string[] } = {
      'bootstrapped': ['bootstrapped'],
      'seed': ['seed'],
      'early': ['seed', 'series-a'],
      'series': ['series-a', 'series-b', 'series-c-plus'],
      'public': ['public'],
      'ipo': ['public'],
      'acquired': ['acquired'],
      'late': ['series-c-plus', 'public'],
      'growth': ['series-b', 'series-c-plus'],
    };

    for (const keyword of keywords) {
      const validStages = stageKeywords[keyword];
      if (validStages && validStages.includes(metadata.company_stage)) {
        return { matches: true, reason: `Stage: ${metadata.company_stage}` };
      }
    }

    return { matches: false, reason: 'Stage mismatch' };
  }

  /**
   * Check if company remote culture matches signal keywords
   */
  private matchesCultureMetadata(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason: string } {
    if (!metadata.remote_culture) {
      return { matches: false, reason: 'Remote culture unknown' };
    }

    const cultureKeywords: { [key: string]: string[] } = {
      'remote-first': ['remote-first'],
      'distributed': ['distributed', 'remote-first'],
      'hybrid': ['hybrid'],
      'flexible': ['hybrid', 'remote-first'],
      'office': ['office-first'],
      'onsite': ['office-first'],
    };

    for (const keyword of keywords) {
      const validCultures = cultureKeywords[keyword];
      if (validCultures && validCultures.includes(metadata.remote_culture)) {
        return { matches: true, reason: `Remote: ${metadata.remote_culture}` };
      }
    }

    return { matches: false, reason: 'Remote culture mismatch' };
  }

  /**
   * Check if company work style matches signal keywords
   */
  private matchesWorkStyleMetadata(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason: string } {
    if (!metadata.work_style || metadata.work_style.length === 0) {
      return { matches: false, reason: 'Work style unknown' };
    }

    const styleKeywords: { [key: string]: string[] } = {
      'fast-paced': ['fast-paced'],
      'fast': ['fast-paced'],
      'balance': ['work-life-balance'],
      'work-life-balance': ['work-life-balance'],
      'async': ['async-first'],
      'agile': ['agile'],
      'collaborative': ['collaborative'],
      'innovative': ['innovative'],
    };

    for (const keyword of keywords) {
      // Direct match
      if (metadata.work_style.includes(keyword)) {
        return { matches: true, reason: `Work style: ${keyword}` };
      }
      // Keyword mapping
      const validStyles = styleKeywords[keyword];
      if (validStyles) {
        for (const style of validStyles) {
          if (metadata.work_style.includes(style)) {
            return { matches: true, reason: `Work style: ${style}` };
          }
        }
      }
    }

    return { matches: false, reason: 'Work style mismatch' };
  }

  /**
   * Check if company benefits match signal keywords
   */
  private matchesBenefitsMetadata(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason: string } {
    if (!metadata.benefits || metadata.benefits.length === 0) {
      return { matches: false, reason: 'Benefits unknown' };
    }

    const benefitKeywords: { [key: string]: string[] } = {
      'equity': ['equity'],
      'stock': ['equity'],
      'pto': ['unlimited-pto'],
      'unlimited': ['unlimited-pto'],
      '4-day': ['4-day-week'],
      'health': ['health-insurance'],
      'insurance': ['health-insurance'],
      'learning': ['learning-budget'],
      'wellness': ['wellness-stipend'],
      '401k': ['401k'],
      'parental': ['parental-leave'],
    };

    for (const keyword of keywords) {
      // Direct match
      if (metadata.benefits.includes(keyword)) {
        return { matches: true, reason: `Benefit: ${keyword}` };
      }
      // Keyword mapping
      const validBenefits = benefitKeywords[keyword];
      if (validBenefits) {
        for (const benefit of validBenefits) {
          if (metadata.benefits.includes(benefit)) {
            return { matches: true, reason: `Benefit: ${benefit}` };
          }
        }
      }
    }

    return { matches: false, reason: 'Benefits mismatch' };
  }

  private matchesCompensation(signal: string, job: Job): boolean {
    const signalLower = signal.toLowerCase();
    
    // Check for minimum salary requirement (e.g., "minimum salary $400k", "salary over 100k", "$150k+")
    const minSalaryMatch = signalLower.match(/(?:minimum|min|over|above|\+|at least)\s*(?:salary)?\s*\$?(\d+(?:\.\d+)?)\s*k?/i) ||
                           signalLower.match(/\$?(\d+(?:\.\d+)?)\s*k?\s*(?:\+|plus|minimum|min)/i) ||
                           signalLower.match(/salary\s*(?:of\s*)?(?:over|above|at least|minimum|min)?\s*\$?(\d+(?:\.\d+)?)\s*k?/i);
    
    if (minSalaryMatch) {
      let minSalary = parseFloat(minSalaryMatch[1]);
      
      // Handle "k" suffix (400k = 400,000)
      // If the number is small (< 1000), assume it's in thousands
      if (minSalary < 1000) {
        minSalary = minSalary * 1000;
      }
      
      // Job must have salary_max that meets or exceeds the minimum requirement
      // We use salary_max because that's the upper bound of what they could pay
      const jobMaxSalary = job.salary_max || job.salary_min || 0;

      if (jobMaxSalary && jobMaxSalary >= minSalary) {
        return true;
      }
      
      // If job has no salary info, we can't confirm it matches
      // Return false to filter it out (conservative approach for dealbreakers)
      return false;
    }
    
    // Check for maximum salary requirement (e.g., "under $100k", "max $80k")
    const maxSalaryMatch = signalLower.match(/(?:under|below|max|maximum|less than)\s*\$?(\d+(?:\.\d+)?)\s*k?/i);
    
    if (maxSalaryMatch) {
      let maxSalary = parseFloat(maxSalaryMatch[1]);
      if (maxSalary < 1000) {
        maxSalary = maxSalary * 1000;
      }
      
      const jobMinSalary = job.salary_min || 0;
      
      // Job's minimum salary should be within user's max budget
      if (jobMinSalary && jobMinSalary <= maxSalary) {
        return true;
      }
      return false;
    }
    
    // Check for salary range preference (e.g., "$100k-150k", "100-150k")
    const rangeMatch = signalLower.match(/\$?(\d+(?:\.\d+)?)\s*k?\s*[-–—to]\s*\$?(\d+(?:\.\d+)?)\s*k?/i);
    
    if (rangeMatch) {
      let rangeLow = parseFloat(rangeMatch[1]);
      let rangeHigh = parseFloat(rangeMatch[2]);
      
      if (rangeLow < 1000) rangeLow = rangeLow * 1000;
      if (rangeHigh < 1000) rangeHigh = rangeHigh * 1000;
      
      const jobMin = job.salary_min || 0;
      const jobMax = job.salary_max || job.salary_min || 0;
      
      // Check if there's any overlap between job range and user's preferred range
      if (jobMax >= rangeLow && jobMin <= rangeHigh) {
        return true;
      }
      return false;
    }
    
    // For signals like "flexible", "negotiable", etc., don't filter
    if (signalLower.includes('flexible') || signalLower.includes('negotiable') || signalLower.includes('open')) {
      return true;
    }
    
    // Default: if we can't parse the signal, don't filter
    return true;
  }

  private matchesSkills(signal: string, job: Job): boolean {
    // Check structured skills and requirements arrays first
    const skills = job.skills || [];
    const requirements = job.requirements || [];
    
    const structuredSkills = [...skills, ...requirements]
      .map(s => s.toLowerCase());

    if (structuredSkills.some(skill => 
      skill.includes(signal) || 
      signal.includes(skill)
    )) {
      return true;
    }

    // Also check text fields for skill mentions that weren't extracted
    const textFields = [
      job.full_description,
      job.short_description,
      ...(job.responsibilities || [])
    ].filter(Boolean).map(t => t!.toLowerCase());

    // For text field matching, require the signal to be a distinct word/phrase
    // to avoid false positives (e.g., "art" matching "startup")
    const signalPattern = new RegExp(`\\b${this.escapeRegex(signal)}\\b`, 'i');
    
    return textFields.some(text => signalPattern.test(text));
  }

  /**
   * Escape special regex characters in a string
   */
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private matchesCulture(signal: string, job: Job): boolean {
    const cultureFields = [
      job.short_description,
      ...(job.responsibilities || []),
      job.company
    ].filter(Boolean).map(c => c!.toLowerCase());

    return cultureFields.some(field => 
      field.includes(signal) || 
      signal.includes(field)
    );
  }

  private matchesGeneral(signal: string, job: Job): boolean {
    const allFields = [
      job.title,
      job.company,
      job.short_description,
      job.location,
      job.display_location,
      ...(job.skills || []),
      ...(job.requirements || []),
      ...(job.responsibilities || [])
    ].filter(Boolean).map(f => f!.toLowerCase());

    return allFields.some(field => 
      field.includes(signal) || 
      signal.includes(field)
    );
  }

  /**
   * Map database result to CalibrationCategory interface
   */
  private mapToCalibrationCategory(data: any): CalibrationCategory {
    const items = data.calibration_signal_items || [];
    
    return {
      id: data.id,
      category: data.category,
      priority: data.priority || 3,
      description: data.description || '',
      excellentFit: items
        .filter((item: any) => item.tier === 'excellent_fit')
        .map((item: any) => this.mapToEnhancedSignal(item)),
      goodFit: items
        .filter((item: any) => item.tier === 'good_fit')
        .map((item: any) => this.mapToEnhancedSignal(item)),
      borderline: items
        .filter((item: any) => item.tier === 'borderline')
        .map((item: any) => this.mapToEnhancedSignal(item)),
      notAFit: items
        .filter((item: any) => item.tier === 'not_a_fit')
        .map((item: any) => this.mapToEnhancedSignal(item))
    };
  }

  /**
   * Map database result to EnhancedSignal interface
   */
  private mapToEnhancedSignal(data: any): EnhancedSignal {
    return {
      id: data.id,
      text: data.text,
      source: data.source,
      confidence: data.confidence || 1.0,
      relatedJobIds: data.related_job_ids || [],
      createdAt: new Date(data.created_at)
    };
  }
}